import { reactive } from 'vue'
import { request } from './api'
import type { JsonObject } from './types'

export interface RuntimeAsset {
  assetId: string
  kind: string
  revision: number
  src: string
  mimeType: string
  sizeBytes: number
  contentHash: string
  metadata: JsonObject
}

interface AssetState {
  loaded: boolean
  loading: boolean
  assets: Record<string, RuntimeAsset>
}

export const runtimeAssets = reactive<AssetState>({
  loaded: false,
  loading: false,
  assets: {},
})

export async function loadRuntimeAssets(): Promise<void> {
  if (runtimeAssets.loading || runtimeAssets.loaded) return
  runtimeAssets.loading = true
  try {
    const payload = await request<{ assets: Record<string, RuntimeAsset> }>(
      '/api/v2/ux/assets',
    )
    runtimeAssets.assets = payload.assets
    runtimeAssets.loaded = true
  } catch {
    // Asset resolution is cosmetic. The package ships a complete local fallback skin.
    runtimeAssets.assets = {}
  } finally {
    runtimeAssets.loading = false
  }
}

const BASE = '/assets/craft-imperium'
const knownIcons = new Set([
  'admin',
  'alliance',
  'army',
  'attack',
  'back',
  'buildings',
  'calculator',
  'chat',
  'check',
  'clay',
  'close',
  'coin',
  'construction',
  'dashboard',
  'down',
  'economy',
  'espionage',
  'events',
  'favorite',
  'friends',
  'grain',
  'home',
  'info',
  'inventory',
  'iron',
  'left',
  'map',
  'map-neutral',
  'map-village',
  'market',
  'messages',
  'military',
  'minus',
  'more',
  'movements',
  'nobility',
  'notifications',
  'plus',
  'population',
  'profile',
  'quests',
  'rankings',
  'recruitment',
  'refresh',
  'reports',
  'research',
  'right',
  'search',
  'settings',
  'smithy',
  'support',
  'training',
  'transport',
  'up',
  'victory',
  'village',
  'warning',
  'wood',
  'worlds',
])
const knownBuildings = new Set([
  'headquarters',
  'woodcutter',
  'quarry',
  'clay_pit',
  'iron_mine',
  'warehouse',
  'barracks',
  'smithy',
  'market',
  'academy',
  'wall',
  'farm',
])

function buildingFallback(type: string): string {
  return `${BASE}/buildings/${knownBuildings.has(type) ? type : 'generic'}.svg`
}

function unitFallback(key: string): string {
  const normalized = key.toLowerCase()
  if (normalized.includes('spear')) return `${BASE}/units/spear.svg`
  if (normalized.includes('sword')) return `${BASE}/units/sword.svg`
  if (normalized.includes('archer') || normalized.includes('bow'))
    return `${BASE}/units/archer.svg`
  if (
    normalized.includes('caval') ||
    normalized.includes('horse') ||
    normalized.includes('light')
  )
    return `${BASE}/units/cavalry.svg`
  if (normalized.includes('ram')) return `${BASE}/units/ram.svg`
  if (normalized.includes('spy') || normalized.includes('scout'))
    return `${BASE}/units/spy.svg`
  if (normalized.includes('noble') || normalized.includes('snob'))
    return `${BASE}/units/noble.svg`
  return `${BASE}/units/generic.svg`
}

export function localAssetUrl(key: string | undefined): string | null {
  if (!key) return null

  if (key.startsWith('ui.icon.')) {
    const icon = key.slice('ui.icon.'.length)
    return knownIcons.has(icon)
      ? `${BASE}/icons/${encodeURIComponent(icon)}.svg`
      : null
  }
  if (['wood', 'clay', 'iron', 'grain', 'population'].includes(key)) {
    return `${BASE}/icons/${key}.svg`
  }
  if (key === 'portal.hero') return `${BASE}/heroes/portal.svg`
  if (key === 'world.dashboard.hero')
    return `${BASE}/heroes/world-dashboard.svg`
  if (key.startsWith('military.') && key.endsWith('.hero')) {
    return `${BASE}/heroes/military.svg`
  }
  if (key.startsWith('building.') && key.endsWith('.hero'))
    return `${BASE}/heroes/building.svg`
  if (key === 'building.catalog.hero' || key === 'building.construction.hero')
    return `${BASE}/heroes/building.svg`
  if (key === 'market.hero') return `${BASE}/heroes/market.svg`
  if (key === 'alliance.hero') return `${BASE}/heroes/alliance.svg`
  if (
    key === 'content.journal.hero' ||
    key === 'inventory.hero' ||
    key === 'victory.hero'
  )
    return `${BASE}/heroes/content.svg`
  if (key === 'ranking.hero') return `${BASE}/heroes/rankings.svg`
  if (key === 'account.hero') return `${BASE}/heroes/account.svg`
  if (key === 'notifications.hero' || key === 'messages.hero')
    return `${BASE}/heroes/notifications.svg`
  if (key === 'admin.hero') return `${BASE}/heroes/admin.svg`
  if (key.endsWith('.hero')) return `${BASE}/heroes/generic.svg`
  if (key === 'village.background' || key === 'village.dashboard.card')
    return `${BASE}/village/default.svg`
  if (key.startsWith('map.village.')) {
    const normalized = key.toLowerCase()
    if (
      normalized.includes('.barbarian_village.') ||
      normalized.includes('.bonus_village.') ||
      normalized.includes('.npc_village.') ||
      normalized.includes('.abandoned_village.')
    )
      return `${BASE}/icons/map-neutral.svg`
    return `${BASE}/village/default.svg`
  }
  if (key.startsWith('world.') && key.endsWith('.portal'))
    return `${BASE}/heroes/portal.svg`
  if (key.startsWith('news.')) return `${BASE}/heroes/generic.svg`
  if (key.startsWith('unit.')) return unitFallback(key.slice('unit.'.length))

  const buildingMatch =
    /^building\.([^.]+)\.(?:village|hero|interface)(?:\.level\.\d+)?$/.exec(key)
  if (buildingMatch?.[1]) return buildingFallback(buildingMatch[1])

  return null
}

export function assetUrl(key: string | undefined): string | null {
  if (!key) return null
  return runtimeAssets.assets[key]?.src ?? localAssetUrl(key)
}

const prefetchedAssets = new Set<string>()

export function prefetchAsset(key: string | undefined): void {
  if (typeof Image === 'undefined') return
  const src = assetUrl(key)
  if (!src || prefetchedAssets.has(src)) return
  prefetchedAssets.add(src)
  const image = new Image()
  image.decoding = 'async'
  image.src = src
}
